import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
  Search, X, Phone, Smartphone, Monitor, Printer, User,
  Building2, ShoppingCart, MapPin, Truck, Package,
  NotebookPen, Loader2, ArrowRight, Zap,
  Camera, Video, HardDrive, Server, Cloud, TrendingUp, Network, CornerDownLeft
} from 'lucide-react';
import { setPendingSearch } from '../hooks/useGlobalSearch';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../i18n/translations';

interface SearchResult {
  module: string;
  moduleLabel: string;
  icon: string;
  color: string;
  id: number;
  primary: string;
  secondary: string;
  badge: string | null;
  searchValue?: string;
}

interface GlobalSearchProps {
  onNavigate: (page: string) => void;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  Phone: <Phone size={16} />,
  Smartphone: <Smartphone size={16} />,
  Monitor: <Monitor size={16} />,
  Printer: <Printer size={16} />,
  User: <User size={16} />,
  Building2: <Building2 size={16} />,
  ShoppingCart: <ShoppingCart size={16} />,
  MapPin: <MapPin size={16} />,
  Truck: <Truck size={16} />,
  Package: <Package size={16} />,
  NotebookPen: <NotebookPen size={16} />,
  Camera: <Camera size={16} />,
  Video: <Video size={16} />,
  HardDrive: <HardDrive size={16} />,
  Server: <Server size={16} />,
  Cloud: <Cloud size={16} />,
  TrendingUp: <TrendingUp size={16} />,
  Network: <Network size={16} />,
};

function Highlight({ text, term }: { text: string; term: string }) {
  if (!term || !text) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <mark className="bg-amber-200 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200 rounded px-1 font-semibold">
        {text.slice(idx, idx + term.length)}
      </mark>
      {text.slice(idx + term.length)}
    </span>
  );
}

function BadgeLabel({ status, lang }: { status: string | null; lang: string }) {
  if (!status) return null;
  const MAP: Record<string, string> = {
    active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300',
    inactive: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
    damaged: 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-300',
    available: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300',
    admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300',
    superadmin: 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-300',
    pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
  };
  const cls = MAP[status] ?? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400';
  const dict = translations[lang as 'tr' | 'en'] as Record<string, string>;
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold border border-current/10 ${cls}`}>
      {dict[`badge.${status}`] ?? status}
    </span>
  );
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ onNavigate }) => {
  const { t, language } = useLanguage();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const modalInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

  const doSearch = useCallback(async (term: string) => {
    if (term.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await axios.get('/api/search', {
        params: { q: term },
        headers: authHeaders(),
      });
      setResults(res.data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(() => doSearch(query.trim()), 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, doSearch]);

  // Outside click for desktop header dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpenDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keyboard shortcut (ESC, Ctrl+K, /)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setModalOpen(false);
        setOpenDropdown(false);
        setQuery('');
      }
      if ((e.ctrlKey && e.key === 'k') || (e.key === '/' && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement))) {
        e.preventDefault();
        openModal();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Custom events for modal trigger
  useEffect(() => {
    const handler = () => openModal();
    window.addEventListener('openMobileSearch', handler);
    window.addEventListener('openGlobalSearch', handler);
    return () => {
      window.removeEventListener('openMobileSearch', handler);
      window.removeEventListener('openGlobalSearch', handler);
    };
  }, []);

  const openModal = () => {
    setModalOpen(true);
    setTimeout(() => modalInputRef.current?.focus(), 50);
  };

  const closeModal = () => {
    setModalOpen(false);
    setQuery('');
    setResults([]);
  };

  const selectResult = (result: SearchResult) => {
    const filterValue = result.searchValue || result.primary;
    setPendingSearch(result.module, filterValue);
    if (result.module === 'notepad') {
      window.history.replaceState(null, '', `/notepad?note=${result.id}`);
    }
    onNavigate(result.module);
    closeModal();
    setOpenDropdown(false);
  };

  const handleCategoryClick = (moduleName: string) => {
    onNavigate(moduleName);
    closeModal();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, -1)); }
    if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); selectResult(results[activeIdx]); }
  };

  // Group results
  const grouped: { module: string; moduleLabel: string; icon: string; color: string; items: SearchResult[] }[] = [];
  results.forEach(r => {
    const g = grouped.find(g => g.module === r.module);
    if (g) g.items.push(r);
    else grouped.push({ module: r.module, moduleLabel: r.moduleLabel, icon: r.icon, color: r.color, items: [r] });
  });

  const totalCount = results.length;

  return (
    <>
      {/* ─── Desktop Header Search Input ─── */}
      <div ref={containerRef} className="hidden lg:flex items-center ml-4 relative">
        <div className="relative">
          {loading
            ? <Loader2 size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-indigo-600 animate-spin" />
            : <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          }
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setOpenDropdown(true); }}
            onFocus={() => setOpenDropdown(true)}
            onKeyDown={handleKeyDown}
            placeholder="Varlık, IMEI, Kullanıcı ara..."
            className="pl-10 pr-12 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white dark:focus:bg-gray-900 w-48 xl:w-72 transition-all"
          />
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {query ? (
              <button onClick={() => { setQuery(''); setResults([]); }} className="text-gray-400 hover:text-gray-600 p-0.5">
                <X size={14} />
              </button>
            ) : (
              <kbd className="hidden xl:inline-block px-1.5 py-0.5 text-[10px] font-semibold text-gray-400 bg-gray-200 dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600">
                ⌘K
              </kbd>
            )}
          </div>
        </div>

        {/* Floating Dropdown for Header */}
        {openDropdown && query.length >= 2 && (
          <div className="absolute top-full left-0 mt-2 w-[480px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-[200]">
            {loading && (
              <div className="flex items-center gap-3 px-4 py-3 text-xs text-gray-500">
                <Loader2 size={14} className="animate-spin text-indigo-600" />
                <span>Aranıyor...</span>
              </div>
            )}
            {!loading && results.length === 0 && (
              <div className="p-6 text-center text-xs text-gray-500">
                "{query}" için eşleşen kayıt bulunamadı.
              </div>
            )}
            {!loading && grouped.length > 0 && (
              <div className="max-h-[380px] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/60">
                {grouped.map(group => (
                  <div key={group.module} className="p-2">
                    <div className="px-2 py-1 text-[11px] font-bold uppercase text-gray-400 tracking-wider">
                      {group.moduleLabel}
                    </div>
                    {group.items.map(res => (
                      <button
                        key={`${res.module}-${res.id}`}
                        onClick={() => selectResult(res)}
                        className="w-full text-left p-2 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-950/40 flex items-center justify-between transition-colors"
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {ICON_MAP[res.icon] || <Package size={14} />}
                          </span>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">
                              <Highlight text={res.primary} term={query} />
                            </p>
                            <p className="text-[11px] text-gray-500 truncate">{res.secondary}</p>
                          </div>
                        </div>
                        <BadgeLabel status={res.badge} lang={language} />
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── Mobile / Trigger Button for Search ─── */}
      <button
        className="lg:hidden p-2 rounded-xl text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        onClick={openModal}
        title="Varlık / IMEI Ara"
      >
        <Search size={20} />
      </button>

      {/* ─── Modern Floating Command Palette Modal Overlay (Raycast / Spotlight Style) ─── */}
      {modalOpen && createPortal(
        <div 
          onClick={closeModal}
          className="fixed inset-0 z-[500] bg-gray-900/60 backdrop-blur-md flex items-start justify-center pt-8 sm:pt-16 px-3 sm:px-4 animate-in fade-in duration-200"
        >
          <div 
            onClick={e => e.stopPropagation()}
            className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col max-h-[82vh] transition-all transform scale-100"
          >
            {/* Search Input Bar */}
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              {loading ? (
                <Loader2 size={20} className="text-indigo-600 animate-spin flex-shrink-0" />
              ) : (
                <Search size={20} className="text-indigo-600 flex-shrink-0" />
              )}
              <input
                ref={modalInputRef}
                type="text"
                value={query}
                onChange={e => { setQuery(e.target.value); setActiveIdx(-1); }}
                onKeyDown={handleKeyDown}
                placeholder="Varlık adı, IMEI, Seri No, Personel veya Şirket ara..."
                className="flex-1 text-sm sm:text-base font-medium bg-transparent outline-none text-gray-900 dark:text-white placeholder-gray-400"
              />
              {query ? (
                <button
                  onClick={() => { setQuery(''); setResults([]); modalInputRef.current?.focus(); }}
                  className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              ) : (
                <span className="text-xs font-semibold px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-400 rounded-lg">
                  ESC
                </span>
              )}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-2 sm:p-3">
              {loading && (
                <div className="flex items-center justify-center gap-2 py-12 text-sm font-medium text-gray-500">
                  <Loader2 size={18} className="animate-spin text-indigo-600" />
                  <span>Sistem genelinde aranıyor...</span>
                </div>
              )}

              {/* No results */}
              {!loading && query.length >= 2 && results.length === 0 && (
                <div className="py-12 text-center">
                  <div className="p-3 bg-gray-100 dark:bg-gray-700/50 rounded-2xl w-12 h-12 mx-auto mb-3 flex items-center justify-center text-gray-400">
                    <Search size={24} />
                  </div>
                  <h4 className="text-sm font-bold text-gray-800 dark:text-gray-200">Sonuç Bulunamadı</h4>
                  <p className="text-xs text-gray-500 mt-1">"{query}" kriterine uygun varlık veya kullanıcı bulunamadı.</p>
                </div>
              )}

              {/* Quick Module Shortcut Badges (when query is empty) */}
              {!loading && query.length < 2 && (
                <div className="p-2 space-y-3">
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider px-1">
                    Hızlı Modül Kısayolları
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[
                      { module: 'computers', label: 'Bilgisayarlar', icon: <Monitor size={16} />, color: 'text-indigo-600 bg-indigo-50 dark:bg-indigo-950/40' },
                      { module: 'mobile-phones', label: 'Cep Telefonları', icon: <Smartphone size={16} />, color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40' },
                      { module: 'monitors', label: 'Monitörler', icon: <Monitor size={16} />, color: 'text-blue-600 bg-blue-50 dark:bg-blue-950/40' },
                      { module: 'printers', label: 'Yazıcılar', icon: <Printer size={16} />, color: 'text-amber-600 bg-amber-50 dark:bg-amber-950/40' },
                      { module: 'gsm-lines', label: 'GSM Hatları', icon: <Phone size={16} />, color: 'text-purple-600 bg-purple-50 dark:bg-purple-950/40' },
                      { module: 'users', label: 'Kullanıcılar', icon: <User size={16} />, color: 'text-pink-600 bg-pink-50 dark:bg-pink-950/40' },
                    ].map(item => (
                      <button
                        key={item.module}
                        onClick={() => handleCategoryClick(item.module)}
                        className="flex items-center gap-2.5 p-3 rounded-xl border border-gray-100 dark:border-gray-700/60 hover:border-indigo-300 dark:hover:border-indigo-700 hover:shadow-sm transition-all text-left group"
                      >
                        <div className={`p-2 rounded-lg ${item.color}`}>
                          {item.icon}
                        </div>
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                          {item.label}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Grouped Search Results */}
              {!loading && grouped.length > 0 && (
                <div className="space-y-3">
                  <div className="px-2 pt-1 flex items-center justify-between text-xs text-gray-400 font-medium">
                    <span>Toplam <b>{totalCount}</b> sonuç bulundu</span>
                    <span className="text-[11px]">Gezinmek için ↑↓ ok tuşlarını kullanın</span>
                  </div>

                  {grouped.map(group => (
                    <div key={group.module} className="bg-gray-50/50 dark:bg-gray-700/20 rounded-xl p-2 border border-gray-100 dark:border-gray-700/50">
                      <div className="flex items-center gap-2 px-2 py-1 mb-1">
                        <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                        <span className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                          {group.moduleLabel} ({group.items.length})
                        </span>
                      </div>

                      <div className="space-y-1">
                        {group.items.map(res => {
                          const flatIdx = results.indexOf(res);
                          const isActive = flatIdx === activeIdx;
                          return (
                            <button
                              key={`m-${res.module}-${res.id}`}
                              onMouseEnter={() => setActiveIdx(flatIdx)}
                              onClick={() => selectResult(res)}
                              className={`w-full text-left p-2.5 rounded-xl flex items-center justify-between transition-all ${
                                isActive 
                                  ? 'bg-indigo-600 text-white shadow-sm' 
                                  : 'hover:bg-white dark:hover:bg-gray-700 text-gray-800 dark:text-gray-200'
                              }`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <div className={`p-2 rounded-lg flex-shrink-0 ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                                  {ICON_MAP[res.icon] || <Package size={16} />}
                                </div>
                                <div className="min-w-0">
                                  <p className={`text-xs font-bold truncate ${isActive ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                                    <Highlight text={res.primary} term={query} />
                                  </p>
                                  {res.secondary && (
                                    <p className={`text-[11px] truncate ${isActive ? 'text-indigo-100' : 'text-gray-500 dark:text-gray-400'}`}>
                                      <Highlight text={res.secondary} term={query} />
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                <BadgeLabel status={res.badge} lang={language} />
                                <CornerDownLeft size={14} className={isActive ? 'text-white' : 'text-gray-400'} />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-xs text-gray-400">
              <span className="flex items-center gap-1.5">
                <Zap size={13} className="text-amber-500" />
                Hızlı Varlık & IMEI Arama Motoru
              </span>
              <span className="hidden sm:inline">Kapatmak için <b>ESC</b> tuşuna basın</span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default GlobalSearch;
